import type { TutorPaymentPolicySelectReturn } from './interfaces'
import type { AppSelectOption } from '@brighterly/lib-core-types/ui/selects/interfaces'
import type { TutorCategoryType } from '@entities/tutor'

import { useAppSelectPaginated } from '@shared/ui/selects'

import { usePaymentPoliciesStore } from './store'
import { usePaymentPoliciesOptionsApi } from '../../api/payment-policy/options.hook'

interface TutorPaymentPolicySelectConfig {
  category: TutorCategoryType
  label: string
  /** Reactive getter for the store field this select binds to. */
  getValue: () => number | null
  /** Setter for the store field this select binds to. */
  setValue: (value: number | null) => void
}

export const useTutorPaymentPolicySelect = (
  config: TutorPaymentPolicySelectConfig,
): TutorPaymentPolicySelectReturn => {
  const placeholder = 'Select here'

  const paymentPoliciesStore = usePaymentPoliciesStore()

  const modelValue = computed<number | undefined>({
    get: () => config.getValue() ?? undefined,
    set: (value) => config.setValue(value ?? null),
  })

  // Saved policy for this category — drives both `selectedOption` (trigger
  // text fallback for off-page values) and the `preselected` query param
  // sent to the backend (so the saved option is guaranteed to appear in the
  // first items page).
  const savedPolicy = computed(() =>
    paymentPoliciesStore.getTutorPolicies.find(p => p.category === config.category),
  )

  // `selectedOption` is a fallback for off-page values (used by AppSelectSearchable
  // when the saved id isn't in the currently rendered `options`). It MUST stay
  // undefined until the first options fetch has completed — otherwise during
  // the loading window the trigger would show `policy.name` ("Policy 27"),
  // which differs from the canonical text in `options[i].text` ("Policy 27 (#49)").
  // Once the fetch resolves, the saved option is in `options` (guaranteed by
  // the `preselected` query param) and AppSelectSearchable's primary lookup
  // (`internalSelectedOption`) takes over with the correct text.
  const selectedOption = computed<AppSelectOption | undefined>(() => {
    if (!firstFetchCompleted.value) return undefined
    const policy = savedPolicy.value
    if (!policy) return undefined
    return { value: policy.id, text: policy.name }
  })

  // Fetcher reads `savedPolicy` lazily — its value is captured at FETCH
  // time, not at hook setup. The factory orchestrates timing so the first
  // fetch fires only AFTER `tutorPolicies` has settled (see watch below).
  const fetcher = ({
    query,
    page,
    signal,
  }: {
    query: string
    page: number
    signal: AbortSignal
  }) => {
    const id = savedPolicy.value?.id ?? null
    return usePaymentPoliciesOptionsApi({
      query,
      page,
      signal,
      preselected: id !== null ? [id] : undefined,
    })
  }

  const {
    options,
    hasMore,
    loading,
    onSearch,
    onLoadMore,
    onOpen,
    refresh,
  } = useAppSelectPaginated(fetcher, { lazy: true })

  // The trigger stays `disabled` until the first options fetch resolves — by then `options`
  // contains the saved option (guaranteed by the `preselected` param), so the trigger can render
  // the canonical "Policy 27 (#49)" text directly from options.
  const firstFetchCompleted = ref<boolean>(false)

  const loadFirstPage = async (): Promise<void> => {
    await refresh()
    firstFetchCompleted.value = true
  }

  void loadFirstPage()

  const disabled = computed<boolean>(() => !firstFetchCompleted.value)

  // Mirror picked option's name into the store so `getDraftEdits` can
  // expose `name` per edit without a separate options cache.
  watch(modelValue, (id) => {
    if (id === undefined || id === null) return
    const option = options.value.find(o => o.value === id)
    if (option && typeof option.text === 'string') {
      paymentPoliciesStore.setSelectedName(config.category, option.text)
    }
  })

  return {
    label: config.label,
    placeholder,
    modelValue,
    options,
    hasMore,
    loading,
    selectedOption,
    disabled,
    onSearch,
    onLoadMore,
    onOpen,
  }
}
